import { alertClasses } from '@mui/material/Alert';
import type { Components, Theme } from '@mui/material/styles';

import type { TintScale } from './looks';
import { SHAPE, tinted, token } from './scales';

const SEVERITY_TINTS: [severity: 'success' | 'error' | 'warning' | 'info', scale: TintScale][] = [
  ['success', 'success'],
  ['error', 'danger'],
  ['warning', 'warning'],
  ['info', 'brand'],
];

/** The kit feedback: alerts and toasts, the progress bar and the empty state. */
export const FEEDBACK: Components<Theme> = {
  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({ ...theme.typography.body1, alignItems: 'flex-start', borderRadius: SHAPE.medium }),
      icon: { color: 'inherit', opacity: 1 },
    },
    variants: [
      {
        props: { variant: 'outlined' },
        style: ({ theme }) => ({
          color: theme.vars.palette.text.primary,
          backgroundColor: theme.vars.palette.background.paper,
          boxShadow: theme.shadows[4],
        }),
      },
      ...SEVERITY_TINTS.flatMap(([severity, scale]) => [
        {
          props: { variant: 'outlined' as const, severity },
          style: ({ theme }: { theme: Theme }) => ({
            borderColor: theme.vars.palette.tint[scale].border,
            [`& .${alertClasses.icon}`]: {
              color: severity === 'warning' ? theme.vars.palette.warning.dark : theme.vars.palette[severity].main,
            },
          }),
        },
        {
          props: { variant: 'standard' as const, severity },
          style: ({ theme }: { theme: Theme }) => ({ border: '1px solid', ...tinted(theme, scale) }),
        },
      ]),
    ],
  },
  MuiAlertTitle: {
    styleOverrides: { root: ({ theme }) => ({ ...theme.typography.subtitle2, marginTop: 0 }) },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: { height: 6, borderRadius: SHAPE.full },
      colorPrimary: ({ theme }) => ({ backgroundColor: theme.vars.palette.divider }),
      bar: { borderRadius: SHAPE.full },
    },
  },
  KitEmptyState: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderStyle: 'dashed',
        borderColor: theme.vars.palette.outline,
        backgroundColor: token('neutral-900', 0.02),
      }),
    },
    variants: [{ props: { plain: true }, style: { borderWidth: 0, backgroundColor: 'transparent' } }],
  },
};
